/**
 * THE NAV'S MARKS, AS THE SCREENS COUNT THEM (provisional P49), from the household every place
 * carries (`HouseholdShown`), which the Worker reads through the one scoped read:
 *
 * - WHAT SHE CAN PAY NOW: the Worker's list of the invoices she can pay from home now (`payable`, a
 *   client's alone), which leaves out any somebody on the household reported sending by Zelle while
 *   the Worker holds it on its way (section 11.7, P53), less any her browser says she has just paid
 *   on Stripe's
 *   page, in this tab or another, whose Pay Payments holds back meanwhile (`onItsWay`, P35), as
 *   the place shown draws them (slice V2). So the count on the
 *   Payments tab is the number of Pay buttons Payments draws, and nothing here reads paid: an
 *   invoice leaves the list only when ops's push says so, for the day her payment is on its way,
 *   or while her report that she sent it by Zelle holds it on its way.
 * - WHAT THE OFFICE HAS SAID since she last opened Messages, as the Worker counts it.
 * - WHETHER A CLEAN WAITS for "How was it?", as the Worker says.
 * - A COUNT is drawn as its number, "9+" past nine, and said in words, "more than 9" past nine, so
 *   the name a tab is read by says what the badge shows.
 */
import type { HouseholdShown, Payable } from '../api.ts';
import { NAV_MARKS } from '../words.ts';
import { onItsWay } from './paying.ts';

/** What the nav marks on its tabs. Each is nothing to mark at 0 or false. */
export interface NavMarks {
  /** The invoices she can pay from here now. */
  readonly toPay: number;
  /** What the office has said since she last opened Messages. */
  readonly newMessages: number;
  /** A clean of hers waits for "How was it?". */
  readonly howWasIt: boolean;
}

/** The most a badge draws as a number: past it, "9+". */
const MOST_DRAWN = 9;

/** Whether an invoice is on its way: as a place draws it, else as her browser keeps it now. */
type OnItsWay = (invoiceId: string) => boolean;

/**
 * Each invoice she can pay from here now, in the Worker's order: its list, less any on its way, as
 * the place draws them (`held`, `useOnItsWay` in `components/Shell.tsx`). Nothing unless the
 * household says she pays here, so a member is never shown money.
 */
export function payableNow(
  household: HouseholdShown | undefined,
  held: OnItsWay = onItsWay,
): readonly Payable[] {
  if (household?.payments !== true) return [];
  return (household.payable ?? []).filter((invoice) => !held(invoice.id));
}

/**
 * Each invoice owed whose payment is on its way, as Payments draws it under On its way (round six,
 * slice S3): the Worker's (`onItsWay`: her household's Zelle word, a bank payment clearing), then
 * those of its list she can pay that her browser says she has just paid on Stripe's page, as the
 * place shown draws them (`held`), each once, with ops's balance. Nothing unless the household says
 * she pays here, so a member is never shown money; and nothing here reads paid.
 */
export function onItsWayNow(
  household: HouseholdShown | undefined,
  held: OnItsWay = onItsWay,
): readonly Payable[] {
  if (household?.payments !== true) return [];
  const told = household.onItsWay ?? [];
  const known = new Set(told.map((invoice) => invoice.id));
  const paidHere = (household.payable ?? []).filter(
    (invoice) => held(invoice.id) && !known.has(invoice.id),
  );
  return [...told, ...paidHere];
}

/** What the nav marks for the household a place is about, and nothing for none. */
export function navMarks(
  household: HouseholdShown | undefined,
  held: OnItsWay = onItsWay,
): NavMarks {
  return {
    toPay: payableNow(household, held).length,
    newMessages: household?.newMessages ?? 0,
    howWasIt: household?.howWasIt === true,
  };
}

/** A count as a badge draws it: its number to nine, and "9+" past it. */
export const countShown = (count: number): string =>
  count > MOST_DRAWN ? `${MOST_DRAWN}+` : String(count);

/** A count as a tab's name says it: its number to nine, and in words past it. */
export const countInWords = (count: number): string =>
  count > MOST_DRAWN ? NAV_MARKS.moreThanNine : String(count);
